import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Th, Td, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarClock, Download, PackageMinus } from "lucide-react";
import WriteOffDialog from "@/components/inventory/WriteOffDialog";
import LotBbdDialog from "@/components/inventory/LotBbdDialog";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";

const ALL = "__all__";

export const StatusBadge = ({ status }) => {
  const { t } = useI18n();
  const tone = {
    expired: "bg-destructive/15 text-destructive border-destructive/30",
    critical: "bg-destructive/10 text-destructive border-destructive/20",
    soon: "bg-amber-500/15 text-amber-700 border-amber-500/30",
    ok: "bg-primary/10 text-primary border-primary/20",
    no_bbd: "bg-muted text-muted-foreground border-border",
  }[status] || "bg-muted text-muted-foreground border-border";
  return (
    <span data-testid={`expiry-status-${status}`}
      className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${tone}`}>
      {t(`ex.status.${status}`)}
    </span>
  );
};

/** Lot-/lieferungsbezogene MHD-Sicht. Rein lesend — es wird nichts gebucht. */
export default function ExpiryTab({ initialStatus }) {
  const { t, fmtDate } = useI18n();
  const { user } = useAuth();
  const canWriteOff = can(user, "inventory:writeOff");
  const canEditBbd = can(user, "inventory:editBbd");
  const [writeOffRow, setWriteOffRow] = useState(null);
  const [bbdRow, setBbdRow] = useState(null);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(initialStatus || "expired,critical,soon,ok");
  const [supplier, setSupplier] = useState("");
  const [warehouse, setWarehouse] = useState("");
  const [location, setLocation] = useState("");
  const [q, setQ] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);

  const params = useCallback(() => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (supplier) p.set("supplier_id", supplier);
    if (warehouse) p.set("warehouse_id", warehouse);
    if (location) p.set("storage_location_id", location);
    if (q.trim()) p.set("q", q.trim());
    return p.toString();
  }, [status, supplier, warehouse, location, q]);

  const load = useCallback(() => {
    api.get(`/inventory/expiry?${params()}`).then((r) => setData(r.data))
      .catch((e) => { toast.error(apiError(e)); setData({ rows: [] }); });
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
    api.get("/storage-locations").then((r) => setLocations(r.data.locations || [])).catch(() => {});
  }, []);

  const locEnabled = !!data?.location_management_enabled;
  const rows = data?.rows || [];
  const counts = data?.counts || {};

  /** Export der AKTUELL gefilterten Ansicht mit fachlichen, lokalisierten Spaltenköpfen.
   *  Datumsangaben werden locale-formatiert (ohne Uhrzeit), interne IDs bleiben außen vor. */
  const exportCsv = () => {
    const cols = [
      ["inv.productSku", (r) => r.sku],
      ["common.name", (r) => r.product_name],
      ["ex.supplier", (r) => r.supplier_name],
      ["ex.supplierLot", (r) => r.lot_number || ""],
      ["ex.receipt", (r) => r.receipt_reference || ""],
      ["ex.receivedAt", (r) => (r.received_at ? fmtDate(r.received_at) : "")],
      ["ex.bbd", (r) => (r.best_before_date ? fmtDate(r.best_before_date) : "")],
      ["ex.daysLeft", (r) => (r.days_left == null ? "" : r.days_left)],
      ["ex.filterStatus", (r) => t(`ex.status.${r.status}`)],
      ["common.quantity", (r) => r.quantity],
      ["ex.quality", (r) => t(`quality.${r.quality}`, r.quality)],
      ["ex.warehouse", (r) => r.warehouse_name || ""],
      ...(locEnabled ? [["ex.location", (r) => r.location_code || ""]] : []),
    ];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [cols.map(([k]) => esc(t(k))).join(";"),
      ...rows.map((r) => cols.map(([, get]) => esc(get(r))).join(";"))].join("\r\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${t("ex.title")}-${data?.today || ""}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="expiry-tab">
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div className="w-full sm:w-52">
          <label className="text-xs text-muted-foreground">{t("ex.filterStatus")}</label>
          <Select value={status || ALL} onValueChange={(v) => setStatus(v === ALL ? "" : v)}>
            <SelectTrigger className="mt-1 h-9" data-testid="expiry-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL} data-testid="expiry-filter-status-all">{t("ex.all")}</SelectItem>
              <SelectItem value="expired,critical,soon,ok" data-testid="expiry-filter-status-tracked">{t("ex.allTracked")}</SelectItem>
              {["expired", "critical", "soon", "ok", "no_bbd"].map((s) => (
                <SelectItem key={s} value={s} data-testid={`expiry-filter-status-${s}`}>
                  {t(`ex.status.${s}`)}{counts[s] != null ? ` (${counts[s]})` : ""}
                </SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-52">
          <label className="text-xs text-muted-foreground">{t("ex.supplier")}</label>
          <Select value={supplier || ALL} onValueChange={(v) => setSupplier(v === ALL ? "" : v)}>
            <SelectTrigger className="mt-1 h-9" data-testid="expiry-filter-supplier"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("ex.all")}</SelectItem>
              {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-44">
          <label className="text-xs text-muted-foreground">{t("ex.warehouse")}</label>
          <Select value={warehouse || ALL} onValueChange={(v) => setWarehouse(v === ALL ? "" : v)}>
            <SelectTrigger className="mt-1 h-9" data-testid="expiry-filter-warehouse"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("ex.all")}</SelectItem>
              {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {locEnabled && (
          <div className="w-full sm:w-44">
            <label className="text-xs text-muted-foreground">{t("ex.location")}</label>
            <Select value={location || ALL} onValueChange={(v) => setLocation(v === ALL ? "" : v)}>
              <SelectTrigger className="mt-1 h-9" data-testid="expiry-filter-location"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("ex.all")}</SelectItem>
                {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="w-full sm:w-52">
          <label className="text-xs text-muted-foreground">{t("inv.productSku")}</label>
          <Input className="mt-1 h-9" value={q} data-testid="expiry-filter-search"
            onChange={(e) => setQ(e.target.value)} />
        </div>
        <Button variant="outline" className="h-9" data-testid="expiry-export-csv"
          onClick={exportCsv} disabled={!rows.length}>
          <Download className="mr-1.5 h-4 w-4" /> {t("ex.exportCsv")}
        </Button>
      </div>

      {!data ? <div className="text-muted-foreground">{t("common.loading")}</div> :
        rows.length === 0 ? <EmptyState testId="expiry-empty" icon={CalendarClock} title={t("ex.title")} hint={t("ex.noRows")} /> : (
          <div className="flex-1 min-h-0 overflow-auto rounded-md border border-border bg-card shadow-sm">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="bg-muted/50"><tr>
                <Th sticky>{t("ex.fefo")}</Th>
                <Th sticky>{t("inv.productSku")}</Th>
                <Th sticky>{t("ex.supplier")}</Th>
                <Th sticky>{t("ex.supplierLot")}</Th>
                <Th sticky>{t("ex.receipt")}</Th>
                <Th sticky>{t("ex.receivedAt")}</Th>
                <Th sticky>{t("ex.bbd")}</Th>
                <Th sticky right>{t("ex.daysLeft")}</Th>
                <Th sticky>{t("ex.filterStatus")}</Th>
                <Th sticky right>{t("common.quantity")}</Th>
                <Th sticky>{t("ex.quality")}</Th>
                <Th sticky>{t("ex.warehouse")}</Th>
                {locEnabled && <Th sticky>{t("ex.location")}</Th>}
                {canWriteOff && <Th sticky right>{t("common.actions")}</Th>}
              </tr></thead>
              <tbody data-testid="expiry-table">
                {rows.map((r, i) => (
                  <tr key={`${r.lot_id || "nolot"}-${r.storage_location_id}-${r.quality}-${i}`}
                    className="hover:bg-muted/40 transition-colors" data-testid={`expiry-row-${i}`}>
                    <Td mono>{r.fefo_rank === 1 && (r.best_before_date || "") ?
                      <span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary"
                        title={t("ex.fefoNext")} data-testid={`expiry-fefo-first-${i}`}>1</span> : r.fefo_rank}</Td>
                    <Td><div className="font-medium text-foreground">{r.product_name}</div>
                      <div className="font-mono text-xs text-muted-foreground">{r.sku}</div></Td>
                    <Td>{r.supplier_name || <span className="text-muted-foreground">{t("ex.noSupplier")}</span>}</Td>
                    <Td mono>{r.lot_number || "—"}</Td>
                    <Td>{r.receipt_reference || "—"}</Td>
                    <Td>{r.received_at ? fmtDate(r.received_at) : "—"}</Td>
                    <Td>{r.best_before_date ? fmtDate(r.best_before_date) : "—"}</Td>
                    <Td right mono>{r.days_left == null ? "—" : r.days_left}</Td>
                    <Td><StatusBadge status={r.status} /></Td>
                    <Td right mono className="font-semibold">{r.quantity}</Td>
                    <Td>{t(`quality.${r.quality}`, r.quality)}</Td>
                    <Td>{r.warehouse_name || "—"}</Td>
                    {locEnabled && <Td mono>{r.location_code || "—"}</Td>}
                    {canWriteOff && (
                      <Td right><div className="flex justify-end gap-1">
                        {canEditBbd && (
                          <Button size="sm" variant="outline" className="h-7"
                            data-testid={`expiry-bbd-${i}`} onClick={() => setBbdRow(r)}>
                            {r.best_before_date ? t("bbd.correct") : t("bbd.assign")}
                          </Button>)}
                        <Button size="sm" variant="outline" className="h-7"
                          data-testid={`write-off-${i}`} onClick={() => setWriteOffRow(r)}>
                          <PackageMinus className="mr-1 h-3.5 w-3.5" />{t("wo.action")}
                        </Button>
                      </div></Td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      <WriteOffDialog open={!!writeOffRow} onClose={() => setWriteOffRow(null)}
        onDone={load} row={writeOffRow} />
      <LotBbdDialog open={!!bbdRow} onClose={() => setBbdRow(null)} onDone={load} row={bbdRow} />
    </div>
  );
}
